import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plug, Eye, EyeOff, CheckCircle2, CreditCard, MessageCircle, MessageSquare } from "lucide-react";

const PLATFORM_COLORS = {
  yemeksepeti: "#FA0050",
  getir: "#5D3EBC",
  trendyol: "#F27A1A",
  migros: "#0A8F3C",
};

const IntegrationCard = ({ item, onSave }) => {
  const [form, setForm] = useState({
    api_key: item.api_key,
    api_secret: item.api_secret,
    store_id: item.store_id,
    enabled: item.enabled,
  });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);

  const color = PLATFORM_COLORS[item.platform] || "#DC2626";

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/integrations/${item.platform}`, form);
      toast.success(`${item.label} ayarları kaydedildi`);
      onSave();
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
    setSaving(false);
  };

  const connected = item.api_key && item.enabled;

  return (
    <div
      data-testid={`integration-card-${item.platform}`}
      className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-xl grid place-items-center text-white font-display font-extrabold"
            style={{ background: color }}
          >
            {item.label.charAt(0)}
          </div>
          <div>
            <p className="font-display font-bold text-lg leading-tight">{item.label}</p>
            {connected ? (
              <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                <CheckCircle2 className="w-3.5 h-3.5" /> Bağlı
              </span>
            ) : (
              <span className="text-xs text-[var(--fb-muted)]">Bağlı değil</span>
            )}
          </div>
        </div>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            data-testid={`integration-toggle-${item.platform}`}
            type="checkbox"
            className="sr-only peer"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
          />
          <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[var(--fb-red)] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
        </label>
      </div>

      <div className="space-y-2">
        <input
          data-testid={`integration-apikey-${item.platform}`}
          value={form.api_key}
          onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
          placeholder="API Key"
          className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
        />
        <div className="relative">
          <input
            data-testid={`integration-secret-${item.platform}`}
            type={showSecret ? "text" : "password"}
            value={form.api_secret}
            onChange={(e) => setForm((f) => ({ ...f, api_secret: e.target.value }))}
            placeholder="API Secret / Token"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg pl-3 pr-9 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <button
            type="button"
            onClick={() => setShowSecret((s) => !s)}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fb-muted)] hover:text-[var(--fb-ink)]"
          >
            {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
        <input
          data-testid={`integration-storeid-${item.platform}`}
          value={form.store_id}
          onChange={(e) => setForm((f) => ({ ...f, store_id: e.target.value }))}
          placeholder="Restoran / Mağaza ID"
          className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
        />
      </div>

      <button
        data-testid={`integration-save-${item.platform}`}
        onClick={save}
        disabled={saving}
        className="w-full bg-[var(--fb-ink)] hover:bg-black disabled:opacity-60 text-white font-semibold py-2.5 rounded-full transition-colors text-sm"
      >
        {saving ? "Kaydediliyor..." : "Kaydet"}
      </button>
    </div>
  );
};

const IyzicoPaymentCard = () => {
  const [form, setForm] = useState({ api_key: "", secret_key: "", sandbox: true, enabled: false });
  const [showSecret, setShowSecret] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/settings/payment");
      setForm({
        api_key: res.data.api_key || "",
        secret_key: res.data.secret_key || "",
        sandbox: res.data.sandbox !== false,
        enabled: !!res.data.enabled,
      });
    } catch (e) {
      toast.error("Ödeme ayarları yüklenemedi");
    }
    setLoaded(true);
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/payment", form);
      toast.success("iyzico ödeme ayarları kaydedildi");
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
    setSaving(false);
  };

  const connected = form.api_key && form.secret_key && form.enabled;

  return (
    <div>
      <div className="flex items-start gap-3 mb-5 bg-[#0d1b3f]/5 border border-[#0d1b3f]/10 rounded-2xl p-4">
        <CreditCard className="w-6 h-6 text-[#1e64ff] shrink-0 mt-0.5" />
        <div>
          <p className="font-display font-bold">Kredi Kartı Ödeme (iyzico)</p>
          <p className="text-sm text-[var(--fb-muted)]">
            iyzico API anahtarlarınızı girerek kioskta kredi kartı ödemesini etkinleştirin.
            Bilgiler yalnızca bu şube için güvenle saklanır.
          </p>
        </div>
      </div>

      <div
        data-testid="payment-card-iyzico"
        className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 space-y-4 max-w-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl grid place-items-center text-white font-display font-extrabold bg-[#1e64ff]">
              iy
            </div>
            <div>
              <p className="font-display font-bold text-lg leading-tight">iyzico</p>
              {connected ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                </span>
              ) : (
                <span className="text-xs text-[var(--fb-muted)]">Pasif</span>
              )}
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              data-testid="payment-toggle-iyzico"
              type="checkbox"
              className="sr-only peer"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[var(--fb-red)] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
          </label>
        </div>

        <div className="space-y-2">
          <input
            data-testid="payment-apikey-iyzico"
            value={form.api_key}
            onChange={(e) => setForm((f) => ({ ...f, api_key: e.target.value }))}
            placeholder="API Key"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <div className="relative">
            <input
              data-testid="payment-secret-iyzico"
              type={showSecret ? "text" : "password"}
              value={form.secret_key}
              onChange={(e) => setForm((f) => ({ ...f, secret_key: e.target.value }))}
              placeholder="Secret Key"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg pl-3 pr-9 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <button
              type="button"
              onClick={() => setShowSecret((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fb-muted)] hover:text-[var(--fb-ink)]"
            >
              {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <label className="flex items-center gap-2 text-sm text-[var(--fb-muted)] pt-1 cursor-pointer">
            <input
              data-testid="payment-sandbox-iyzico"
              type="checkbox"
              checked={form.sandbox}
              onChange={(e) => setForm((f) => ({ ...f, sandbox: e.target.checked }))}
              className="w-4 h-4 accent-[var(--fb-red)]"
            />
            Test ortamı (sandbox) — kapatınca canlı ortam kullanılır
          </label>
        </div>

        <button
          data-testid="payment-save-iyzico"
          onClick={save}
          disabled={saving || !loaded}
          className="w-full bg-[var(--fb-ink)] hover:bg-black disabled:opacity-60 text-white font-semibold py-2.5 rounded-full transition-colors text-sm"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
};

const WhatsAppCard = () => {
  const [form, setForm] = useState({
    access_token: "", phone_number_id: "", template_name: "payment_confirmation",
    template_language: "en_US", api_version: "v25.0", enabled: false,
  });
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    try {
      const res = await api.get("/settings/whatsapp");
      setForm({
        access_token: res.data.access_token || "",
        phone_number_id: res.data.phone_number_id || "",
        template_name: res.data.template_name || "payment_confirmation",
        template_language: res.data.template_language || "en_US",
        api_version: res.data.api_version || "v25.0",
        enabled: !!res.data.enabled,
      });
    } catch (e) {
      toast.error("WhatsApp ayarları yüklenemedi");
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/whatsapp", form);
      toast.success("WhatsApp ayarları kaydedildi");
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
    setSaving(false);
  };

  const connected = form.access_token && form.phone_number_id && form.enabled;

  return (
    <div>
      <div className="flex items-start gap-3 mb-5 bg-[#25D366]/10 border border-[#25D366]/20 rounded-2xl p-4">
        <MessageCircle className="w-6 h-6 text-[#128C7E] shrink-0 mt-0.5" />
        <div>
          <p className="font-display font-bold">WhatsApp Sipariş Onayı (Meta Cloud API)</p>
          <p className="text-sm text-[var(--fb-muted)]">
            Kredi kartı ödemesi başarılı olunca müşteriye WhatsApp'tan otomatik sipariş numarası gönderilir.
            Meta Business hesabınızdan alınan bilgileri girin. Onaylı bir mesaj şablonu gereklidir.
          </p>
        </div>
      </div>

      <div
        data-testid="whatsapp-card"
        className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 space-y-4 max-w-xl"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl grid place-items-center text-white bg-[#25D366]">
              <MessageCircle className="w-6 h-6" />
            </div>
            <div>
              <p className="font-display font-bold text-lg leading-tight">WhatsApp</p>
              {connected ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                </span>
              ) : (
                <span className="text-xs text-[var(--fb-muted)]">Pasif</span>
              )}
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              data-testid="whatsapp-toggle"
              type="checkbox"
              className="sr-only peer"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-[#25D366] transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
          </label>
        </div>

        <div className="space-y-2">
          <div className="relative">
            <input
              data-testid="whatsapp-token"
              type={showToken ? "text" : "password"}
              value={form.access_token}
              onChange={(e) => setForm((f) => ({ ...f, access_token: e.target.value }))}
              placeholder="Kalıcı Erişim Token (Access Token)"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg pl-3 pr-9 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <button
              type="button"
              onClick={() => setShowToken((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fb-muted)] hover:text-[var(--fb-ink)]"
            >
              {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <input
            data-testid="whatsapp-phone-id"
            value={form.phone_number_id}
            onChange={(e) => setForm((f) => ({ ...f, phone_number_id: e.target.value }))}
            placeholder="Telefon Numarası ID (Phone Number ID)"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              data-testid="whatsapp-template-name"
              value={form.template_name}
              onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
              placeholder="Şablon adı"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <input
              data-testid="whatsapp-template-lang"
              value={form.template_language}
              onChange={(e) => setForm((f) => ({ ...f, template_language: e.target.value }))}
              placeholder="Şablon dili (ör. tr, en_US)"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
            />
          </div>
          <p className="text-xs text-[var(--fb-muted)]">
            Şablon gövdesinde 1 değişken ({"{{1}}"}) sipariş numarasıdır. Örn: "Siparişiniz alındı. Sipariş No: {"{{1}}"}"
          </p>
        </div>

        <button
          data-testid="whatsapp-save"
          onClick={save}
          disabled={saving || !loaded}
          className="w-full bg-[var(--fb-ink)] hover:bg-black disabled:opacity-60 text-white font-semibold py-2.5 rounded-full transition-colors text-sm"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
};

export const NetgsmSmsCard = () => {
  const [form, setForm] = useState({ usercode: "", password: "", msgheader: "", brand_name: "", template: "{marka} - Siparisiniz alindi. Siparis No: {no}", enabled: false });
  const [showPass, setShowPass] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [testPhone, setTestPhone] = useState("");
  const [testing, setTesting] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  const fetchBalance = async () => {
    setBalLoading(true);
    try {
      const res = await api.get("/settings/netgsm-global/balance");
      if (res.data.ok) setBalance(res.data);
      else setBalance(null);
    } catch {
      setBalance(null);
    }
    setBalLoading(false);
  };

  const load = async () => {
    try {
      const res = await api.get("/settings/netgsm-global");
      setForm({
        usercode: res.data.usercode || "",
        password: res.data.password || "",
        msgheader: res.data.msgheader || "",
        brand_name: res.data.brand_name || "",
        template: res.data.template || "{marka} - Siparisiniz alindi. Siparis No: {no}",
        enabled: !!res.data.enabled,
      });
      if (res.data.usercode && res.data.password) fetchBalance();
    } catch (e) {
      toast.error("SMS ayarları yüklenemedi");
    }
    setLoaded(true);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/netgsm-global", form);
      toast.success("Netgsm SMS ayarları kaydedildi");
      if (form.usercode && form.password) fetchBalance();
    } catch (e) {
      toast.error("Kaydedilemedi");
    }
    setSaving(false);
  };

  const sendTest = async () => {
    const p = testPhone.replace(/\D/g, "");
    if (p.length !== 10) {
      toast.error("Test için 10 haneli telefon girin");
      return;
    }
    if (!form.usercode || !form.password || !form.msgheader) {
      toast.error("Önce kullanıcı adı, şifre ve gönderici başlığını doldurun");
      return;
    }
    setTesting(true);
    try {
      const res = await api.post("/settings/netgsm-global/test", { ...form, phone: p });
      if (res.data.ok) toast.success("Test SMS gönderildi ✓ Telefonu kontrol edin");
      else toast.error("Test başarısız: " + (res.data.detail || res.data.code || "bilinmeyen hata"));
    } catch (e) {
      toast.error("Test gönderilemedi");
    }
    setTesting(false);
  };

  const connected = form.usercode && form.password && form.msgheader && form.enabled;

  return (
    <div>
      <div className="flex items-start gap-3 mb-5 bg-indigo-50 border border-indigo-100 rounded-2xl p-4">
        <MessageSquare className="w-6 h-6 text-indigo-600 shrink-0 mt-0.5" />
        <div>
          <p className="font-display font-bold">SMS Sipariş Onayı (Netgsm)</p>
          <p className="text-sm text-[var(--fb-muted)]">
            Online (link) sipariş oluşunca müşteriye Netgsm üzerinden otomatik "Siparişiniz alındı · Sipariş No" SMS'i gönderilir.
            Netgsm panelinizden aldığınız API alt kullanıcı bilgilerini ve onaylı gönderici başlığını girin.
          </p>
        </div>
      </div>

      <div data-testid="netgsm-card" className="bg-white rounded-2xl border border-[var(--fb-border)] p-5 space-y-4 max-w-xl">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl grid place-items-center text-white bg-indigo-600">
              <MessageSquare className="w-6 h-6" />
            </div>
            <div>
              <p className="font-display font-bold text-lg leading-tight">Netgsm SMS</p>
              {connected ? (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-600">
                  <CheckCircle2 className="w-3.5 h-3.5" /> Aktif
                </span>
              ) : (
                <span className="text-xs text-[var(--fb-muted)]">Pasif</span>
              )}
            </div>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              data-testid="netgsm-toggle"
              type="checkbox"
              className="sr-only peer"
              checked={form.enabled}
              onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            />
            <div className="w-11 h-6 bg-gray-300 rounded-full peer peer-checked:bg-indigo-600 transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" />
          </label>
        </div>

        <div data-testid="netgsm-balance" className="rounded-xl border border-[var(--fb-border)] bg-[var(--fb-neutral)] p-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-[var(--fb-muted)]">SMS Bakiyesi</p>
            {balance ? (
              <p className="font-display font-bold text-lg leading-tight">
                {balance.total_sms} SMS
                {balance.tl_balance != null && (
                  <span className="text-sm font-semibold text-[var(--fb-muted)]"> · ₺{balance.tl_balance}</span>
                )}
              </p>
            ) : (
              <p className="text-sm text-[var(--fb-muted)]">Bilgileri kaydedince görünür</p>
            )}
            {balance && balance.total_sms <= 50 && (
              <p data-testid="netgsm-balance-warning" className="text-xs font-semibold text-[var(--fb-red)] mt-0.5">
                ⚠ Bakiye azaldı — lütfen SMS paketinizi yükleyin.
              </p>
            )}
          </div>
          <button
            type="button"
            data-testid="netgsm-balance-refresh"
            onClick={fetchBalance}
            disabled={balLoading}
            className="text-xs font-semibold text-indigo-600 border border-indigo-600 hover:bg-indigo-50 disabled:opacity-60 px-3 py-1.5 rounded-full transition-colors whitespace-nowrap"
          >
            {balLoading ? "..." : "Yenile"}
          </button>
        </div>

        <div className="space-y-2">
          <input
            data-testid="netgsm-usercode"
            value={form.usercode}
            onChange={(e) => setForm((f) => ({ ...f, usercode: e.target.value }))}
            placeholder="Netgsm kullanıcı adı / abone no"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <div className="relative">
            <input
              data-testid="netgsm-password"
              type={showPass ? "text" : "password"}
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="API şifresi"
              className="w-full text-sm border border-[var(--fb-border)] rounded-lg pl-3 pr-9 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <button
              type="button"
              onClick={() => setShowPass((s) => !s)}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--fb-muted)] hover:text-[var(--fb-ink)]"
            >
              {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          <input
            data-testid="netgsm-msgheader"
            value={form.msgheader}
            onChange={(e) => setForm((f) => ({ ...f, msgheader: e.target.value }))}
            placeholder="Onaylı gönderici başlığı (msgheader)"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <input
            data-testid="netgsm-brand"
            value={form.brand_name}
            onChange={(e) => setForm((f) => ({ ...f, brand_name: e.target.value }))}
            placeholder="Marka adı (örn. Deny's Burger)"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
          />
          <textarea
            data-testid="netgsm-template"
            value={form.template}
            onChange={(e) => setForm((f) => ({ ...f, template: e.target.value }))}
            rows={2}
            placeholder="Mesaj şablonu"
            className="w-full text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)] resize-none"
          />
          <p className="text-xs text-[var(--fb-muted)]">
            Yer tutucular: <span className="font-mono font-semibold">{"{marka}"}</span> marka adı ·{" "}
            <span className="font-mono font-semibold">{"{sube}"}</span> şube adı ·{" "}
            <span className="font-mono font-semibold">{"{no}"}</span> sipariş no. Örn: "{"{marka} {sube} - Siparisiniz alindi. No: {no}"}"
          </p>
          <p className="text-xs text-[var(--fb-muted)]">
            Yalnızca online (link) siparişlerde, telefonu olan müşterilere gönderilir. Kredi kartı ödemesi onaylanmayan siparişlere SMS gitmez.
          </p>
        </div>

        <div className="border-t border-[var(--fb-border)] pt-3 space-y-2">
          <p className="text-xs font-semibold text-[var(--fb-muted)]">Test SMS — ayarları anında doğrulayın</p>
          <div className="flex gap-2">
            <input
              data-testid="netgsm-test-phone"
              value={testPhone}
              inputMode="numeric"
              maxLength={10}
              onChange={(e) => setTestPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
              placeholder="Test telefonu (10 haneli)"
              className="flex-1 min-w-0 text-sm border border-[var(--fb-border)] rounded-lg px-3 py-2 outline-none focus:border-[var(--fb-red)]"
            />
            <button
              data-testid="netgsm-test-send"
              onClick={sendTest}
              disabled={testing}
              className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 border border-indigo-600 hover:bg-indigo-50 disabled:opacity-60 px-4 py-2 rounded-full transition-colors whitespace-nowrap"
            >
              <MessageSquare className="w-4 h-4" /> {testing ? "Gönderiliyor..." : "Test SMS Gönder"}
            </button>
          </div>
        </div>

        <button
          data-testid="netgsm-save"
          onClick={save}
          disabled={saving || !loaded}
          className="w-full bg-[var(--fb-ink)] hover:bg-black disabled:opacity-60 text-white font-semibold py-2.5 rounded-full transition-colors text-sm"
        >
          {saving ? "Kaydediliyor..." : "Kaydet"}
        </button>
      </div>
    </div>
  );
};

export const IntegrationsPanel = () => {
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const res = await api.get("/integrations");
      setItems(res.data);
    } catch (e) {
      toast.error("Entegrasyonlar yüklenemedi");
    }
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="space-y-8">
      <IyzicoPaymentCard />

      <WhatsAppCard />

      <div>
        <div className="flex items-start gap-3 mb-5 bg-red-50 border border-red-100 rounded-2xl p-4">
          <Plug className="w-6 h-6 text-[var(--fb-red)] shrink-0 mt-0.5" />
          <div>
            <p className="font-display font-bold">Yemek Platformu Entegrasyonları</p>
            <p className="text-sm text-[var(--fb-muted)]">
              Yemeksepeti, Getir, Trendyol ve Migros Yemek hesaplarınızın API bilgilerini
              girerek gelen siparişleri tek panelden yönetin.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((item) => (
            <IntegrationCard key={item.platform} item={item} onSave={load} />
          ))}
        </div>
      </div>
    </div>
  );
};
